import React from 'react';
import { IContractTimelineSegment } from '../../../../types/contracts/contractTypes';
import useTranslate from '../../../../customHooks/useTranslate';
import Title from '../../../components/Title';
import DotIcon from '../../../components/DotIcon';
import Menu from '../../components/Menu';
import Icon from '../../../../muiComponents/Icon';
import { formatDate } from '../../../../utils/formatDates';
import useLanguage from '../../../../customHooks/useLanguage';
import Button from '../../../../muiComponents/Button';
import '../../../../stylesheets/stylesComponents/timeline.scss';
import { useContractDetail } from '../../../contextsProperty/ContextContractDetail';
import { getRandomNumber } from '../../../../utils/usefulFunctions';
import useUserProfile from '../../../../customHooks/useUserProfile';

interface TimelineCardProps {
    segment: IContractTimelineSegment;
    disabled: boolean;
    onDelete: (id: string) => void;
    onDisable: () => Promise<void>;
}

const TimelineCard = ({
    segment,
    disabled,
    onDelete,
    onDisable,
}: TimelineCardProps) => {
    const translate = useTranslate();
    const language = useLanguage();
    const { readOnlyUser } = useUserProfile();

    const { handleOpenSeasonPanel } = useContractDetail();

    return (
        <div className='timeline-element' key={segment.id}>
            <div
                className='timeline-segment'
                style={{
                    backgroundColor: segment.color,
                }}
            >
                <div
                    className='timeline-dot'
                    style={{ borderColor: segment.color }}
                />
                <div
                    className='timeline-dot--end'
                    style={{ borderColor: segment.color }}
                />
            </div>

            {segment.id ? (
                <div
                    className='timeline-card'
                    style={{ borderColor: segment.color }}
                >
                    <div className='flex flex-nowrap flex-align-center'>
                        <div
                            className='flex flex-align-center gap-5 flex-between flex-wrap'
                            style={{ flexGrow: 1 }}
                        >
                            <Title>{segment.name}</Title>
                            <div className='flex flex-align-center gap-5'>
                                <DotIcon
                                    truthy={segment.enabled}
                                    falsy={!segment.enabled}
                                />
                                <small>
                                    {segment.enabled
                                        ? translate('enabled')
                                        : translate('disabled')}
                                </small>
                            </div>
                        </div>
                        <Menu
                            menuItems={[
                                {
                                    text: readOnlyUser ? 'view' : 'edit',
                                    icon: readOnlyUser ? 'Visibility' : 'Edit',
                                    onClick: () => {
                                        handleOpenSeasonPanel({
                                            id:
                                                segment.id ||
                                                `-${getRandomNumber()}`,
                                            name: segment.name,
                                            start: segment.start,
                                            end: segment.end,
                                            enabled: segment.enabled,
                                            __isAdded: false,
                                            __isChanged: true,
                                        });
                                    },
                                },
                                {
                                    text: segment.enabled
                                        ? 'disabled'
                                        : 'enabled',
                                    icon: segment.enabled
                                        ? 'NotInterested'
                                        : 'Circle',
                                    disabled: disabled,
                                    onClick: async () => {
                                        await onDisable();
                                    },
                                },
                                {
                                    text: 'delete',
                                    icon: 'Delete',
                                    disabled: disabled,
                                    onClick: () => {
                                        onDelete(segment.id as string);
                                    },
                                },
                            ]}
                        />
                    </div>

                    <div className='flex flex-align-center gap-10 text-overflow-ellipsis'>
                        <Icon
                            iconName='DateRange'
                            className='text-seconday-700'
                        />
                        <p className='text-overflow-ellipsis'>
                            {formatDate(segment.start, language)} -{' '}
                            {formatDate(segment.end, language)}
                        </p>
                    </div>
                </div>
            ) : (
                <div className='timeline-card--empty'>
                    <Button
                        startIcon='Add'
                        onClick={() => {
                            const data = {
                                start: segment.start,
                                __isAdded: true,
                                __isChanged: true,
                            };
                            handleOpenSeasonPanel(data);
                        }}
                        disabled={disabled}
                    >
                        {translate('add')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default TimelineCard;
